<template>
  <div id="app">
    <!-- Header -->
    <header class="header" :class="{ 'scrolled': isScrolled }">
      <nav class="nav-container">
        <a href="#home" class="logo" @click.prevent="scrollTo('home')">
          <img src="logo.jpeg" alt="Oasis Finance Logo" @error="handleLogoError">
          <div class="logo-text">
            <span class="brand">OASIS FINANCE</span>
            <span class="tagline">Votre partenaire financier</span>
          </div>
        </a>

        <ul class="nav-menu" :class="{ 'active': isMenuOpen }">
          <li><a href="#home" @click.prevent="scrollToAndClose('home')">Accueil</a></li>
          <li><a href="#about" @click.prevent="scrollToAndClose('about')">Qui sommes-nous</a></li>
          <li><a href="#services" @click.prevent="scrollToAndClose('services')">Nos services</a></li>
          <li><a href="#team" @click.prevent="scrollToAndClose('team')">Notre équipe</a></li>
          <li><a href="#contact" @click.prevent="scrollToAndClose('contact')">Contact</a></li>
        </ul>

        <div class="nav-cta">
          <a href="#contact" class="btn btn-primary" @click.prevent="scrollTo('contact')">
            Nous contacter
          </a>
        </div>

        <button class="mobile-toggle" @click="toggleMenu" aria-label="Menu">
          <span :class="{ 'active': isMenuOpen }"></span>
          <span :class="{ 'active': isMenuOpen }"></span>
          <span :class="{ 'active': isMenuOpen }"></span>
        </button>
      </nav>
    </header>

    <!-- Main Content -->
    <main>
      <HeroSection id="home" />
      <PartnersSection />
      <WhySection id="about" />
      <ServicesSection id="services" />
      <AboutSection />
      <GallerySection id="team" />
      <ContactSection id="contact" />
    </main>

    <!-- Footer -->
    <footer class="footer">
      <div class="footer-container">
        <div class="footer-grid">
          <div class="footer-brand">
            <div class="logo">
              <img src="/logo.jpeg" alt="Oasis Finance Logo" @error="handleLogoError">
              <div class="logo-text">
                <span class="brand">OASIS FINANCE</span>
                <span class="tagline">Votre partenaire financier</span>
              </div>
            </div>
            <p>
              Institution de microfinance dédiée à l'inclusion financière 
              au Cameroun. Ensemble, construisons votre avenir financier.
            </p>
            <div class="footer-social">
              <a href="#" aria-label="Facebook">
                <svg viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
              </a>
              <a href="#" aria-label="WhatsApp">
                <svg viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
              </a>
              <a href="#" aria-label="LinkedIn">
                <svg viewBox="0 0 24 24"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
              </a>
            </div>
          </div>

          <div class="footer-column">
            <h4>Services</h4>
            <ul>
              <li><a href="#services" @click.prevent="scrollTo('services')">Prêts personnels</a></li>
              <li><a href="#services" @click.prevent="scrollTo('services')">Microcrédit</a></li>
              <li><a href="#services" @click.prevent="scrollTo('services')">Épargne</a></li>
              <li><a href="#services" @click.prevent="scrollTo('services')">Crédit immobilier</a></li>
              <li><a href="#services" @click.prevent="scrollTo('services')">Transferts</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Entreprise</h4>
            <ul>
              <li><a href="#about" @click.prevent="scrollTo('about')">À propos</a></li>
              <li><a href="#team" @click.prevent="scrollTo('team')">Notre équipe</a></li>
              <li><a href="#contact" @click.prevent="scrollTo('contact')">Carrières</a></li>
              <li><a href="#">Partenariats</a></li>
            </ul>
          </div>

          <div class="footer-column">
            <h4>Support</h4>
            <ul>
              <li><a href="#contact" @click.prevent="scrollTo('contact')">Nous contacter</a></li>
              <li><a href="#">FAQ</a></li>
              <li><a href="#">Conditions générales</a></li>
              <li><a href="#">Politique de confidentialité</a></li>
            </ul>
          </div>
        </div>

        <div class="footer-bottom">
          <p>© 2019-2025 OASIS FINANCE. Tous droits réservés.</p>
          <p>Développé avec ❤️ au Cameroun</p>
        </div>
      </div>
    </footer>

    <!-- Scroll to Top -->
    <button 
      class="scroll-top" 
      :class="{ 'visible': showScrollTop }" 
      @click="scrollToTop"
      aria-label="Retour en haut"
    >
      <svg viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15"/></svg>
    </button>
  </div>
</template>

<script>
import HeroSection from './components/HeroSection.vue'
import PartnersSection from './components/PartnersSection.vue'
import WhySection from './components/WhySection.vue'
import ServicesSection from './components/ServicesSection.vue'
import AboutSection from './components/AboutSection.vue'
import GallerySection from './components/GallerySection.vue'
import ContactSection from './components/ContactSection.vue'

export default {
  name: 'App',
  components: {
    HeroSection,
    PartnersSection,
    WhySection,
    ServicesSection,
    AboutSection,
    GallerySection,
    ContactSection
  },
  data() {
    return {
      isScrolled: false,
      isMenuOpen: false,
      showScrollTop: false
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 100
      this.showScrollTop = window.scrollY > 300
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    scrollTo(id) {
      const element = document.getElementById(id)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    scrollToAndClose(id) {
      this.scrollTo(id)
      this.isMenuOpen = false
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    handleLogoError(e) {
      e.target.style.display = 'none'
    }
  }
}
</script>

<style>
:root {
  /* Colors - Oasis Finance Brand */
  --primary-blue: #0A2C5E;
  --primary-blue-light: #1a4a8a;
  --accent-blue: #2563EB;
  --accent-gold: #F59E0B;
  --accent-green: #10B981;
  --white: #FFFFFF;
  --off-white: #F8FAFC;
  --gray-50: #F1F5F9;
  --gray-100: #E2E8F0;
  --gray-200: #CBD5E1;
  --gray-400: #94A3B8;
  --gray-500: #64748B;
  --gray-600: #475569;
  --gray-700: #334155;
  --gray-800: #1E293B;
  --gray-900: #0F172A;
  
  /* Typography */
  --font-display: 'Outfit', sans-serif;
  --font-body: 'Plus Jakarta Sans', sans-serif;
  
  /* Transitions */
  --transition-fast: 0.15s ease;
  --transition-base: 0.3s ease;
  --transition-slow: 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  
  /* Shadows */
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  --shadow-2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: var(--font-body);
  color: var(--gray-800);
  background: var(--white);
  line-height: 1.6;
  overflow-x: hidden;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ==================== HEADER ==================== */
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: var(--transition-base);
}

.header.scrolled {
  box-shadow: var(--shadow-md);
}

.nav-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0.75rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
}

.logo img {
  height: 50px;
  width: auto;
  border-radius: 8px;
}

.logo-text {
  display: flex;
  flex-direction: column;
}

.logo-text .brand {
  font-family: var(--font-display);
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-blue);
  letter-spacing: -0.02em;
}

.logo-text .tagline {
  font-size: 0.7rem;
  color: var(--gray-600);
  font-weight: 500;
}

.nav-menu {
  display: flex;
  list-style: none;
  gap: 0.5rem;
}

.nav-menu a {
  display: block;
  padding: 0.625rem 1.25rem;
  text-decoration: none;
  color: var(--gray-600);
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 8px;
  transition: var(--transition-fast);
}

.nav-menu a:hover {
  color: var(--primary-blue);
  background: var(--gray-50);
}

.nav-cta {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-family: var(--font-body);
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: var(--transition-base);
}

.btn-primary {
  background: var(--primary-blue);
  color: var(--white);
}

.btn-primary:hover {
  background: var(--primary-blue-light);
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.mobile-toggle {
  display: none;
  flex-direction: column;
  gap: 5px;
  padding: 8px;
  background: none;
  border: none;
  cursor: pointer;
}

.mobile-toggle span {
  width: 24px;
  height: 2px;
  background: var(--gray-800);
  border-radius: 2px;
  transition: var(--transition-base);
}

.mobile-toggle span.active:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.mobile-toggle span.active:nth-child(2) {
  opacity: 0;
}

.mobile-toggle span.active:nth-child(3) {
  transform: rotate(-45deg) translate(5px, -5px);
}

/* ==================== FOOTER ==================== */
.footer {
  background: var(--gray-900);
  color: var(--white);
  padding: 4rem 2rem 2rem;
}

.footer-container {
  max-width: 1400px;
  margin: 0 auto;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 4rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-brand .logo {
  margin-bottom: 1rem;
}

.footer-brand .logo img {
  height: 45px;
}

.footer-brand .logo-text .brand {
  color: var(--white);
}

.footer-brand .logo-text .tagline {
  color: var(--gray-400);
}

.footer-brand > p {
  margin-top: 1rem;
  font-size: 0.95rem;
  color: var(--gray-400);
  line-height: 1.7;
}

.footer-social {
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.footer-social a {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  transition: var(--transition-base);
}

.footer-social a:hover {
  background: var(--primary-blue);
  transform: translateY(-3px);
}

.footer-social svg {
  width: 18px;
  height: 18px;
  fill: var(--white);
}

.footer-column h4 {
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.footer-column ul {
  list-style: none;
}

.footer-column li {
  margin-bottom: 0.75rem;
}

.footer-column a {
  color: var(--gray-400);
  text-decoration: none;
  font-size: 0.9rem;
  transition: var(--transition-fast);
}

.footer-column a:hover {
  color: var(--white);
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 2rem;
}

.footer-bottom p {
  font-size: 0.85rem;
  color: var(--gray-500);
}

/* ==================== SCROLL TO TOP ==================== */
.scroll-top {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 50px;
  height: 50px;
  background: var(--primary-blue);
  color: var(--white);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  transition: var(--transition-base);
  box-shadow: var(--shadow-lg);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.scroll-top.visible {
  opacity: 1;
  visibility: visible;
}

.scroll-top:hover {
  background: var(--primary-blue-light);
  transform: translateY(-5px);
}

.scroll-top svg {
  width: 24px;
  height: 24px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
}

/* ==================== RESPONSIVE ==================== */
@media (max-width: 1024px) {
  .footer-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
}

@media (max-width: 768px) {
  .nav-menu {
    position: fixed;
    top: 80px;
    left: 0;
    right: 0;
    background: var(--white);
    flex-direction: column;
    padding: 2rem;
    gap: 0;
    transform: translateY(-150%);
    transition: var(--transition-base);
    box-shadow: var(--shadow-lg);
  }

  .nav-menu.active {
    transform: translateY(0);
  }

  .nav-menu a {
    padding: 1rem;
  }

  .nav-cta {
    display: none;
  }

  .mobile-toggle {
    display: flex;
  }

  .footer-grid {
    grid-template-columns: 1fr;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}
</style>
